import { getReceiverCountry, isLoggedInUser } from '../utilsV3/authUtils';
import {
  getEncryptedAccountNum,
  getUserType,
  isDonateButton,
  isHostedFlow,
} from '../utilsV3/buttonFactoryUtils';
import { getCharityName, isCampaign } from './campaignUtils';

const isHosted = isHostedFlow();
const isCampaignFlow = isCampaign();

const PAGE_SPEC = {
  LANDING_PAGE: {
    pgrp: 'main:wps:donate:landing:login',
    page: 'main:wps:donate:landing:login',
  },
  MANAGE_PAGE: {
    pgrp: 'main:wps:donate:button_manager',
    page: 'main:wps:donate:button_manager',
  },
  OVERVIEW_PAGE: {
    pgrp: 'main:wps:donate:button_overview',
    page: 'main:wps:donate:button_overview',
  },
  CAMPAIGN_OVERVIEW_PAGE: {
    pgrp: 'main:wps:donate:campaign_overview',
    page: 'main:wps:donate:campaign_overview',
  },
  WIZARD_STEP_ONE_HOSTED_PAGE: {
    step: 1,
    pgrp: 'main:wps:donate:button:setup:wizard:hosted:1',
    page: 'main:wps:donate:button:setup:wizard:hosted:1',
  },
  WIZARD_STEP_TWO_HOSTED_PAGE: {
    step: 2,
    pgrp: 'main:wps:donate:button:setup:wizard:hosted:2',
    page: 'main:wps:donate:button:setup:wizard:hosted:2',
  },
  WIZARD_STEP_THREE_HOSTED_PAGE: {
    step: 3,
    pgrp: 'main:wps:donate:button:setup:wizard:hosted:3',
    page: 'main:wps:donate:button:setup:wizard:hosted:3',
  },
  WIZARD_STEP_FOUR_HOSTED_PAGE: {
    step: 4,
    pgrp: 'main:wps:donate:button:setup:wizard:hosted:4',
    page: 'main:wps:donate:button:setup:wizard:hosted:4',
  },
  WIZARD_STEP_ONE_UNHOSTED_PAGE: {
    step: 1,
    pgrp: 'main:wps:donate:button:setup:wizard:unhosted:1',
    page: 'main:wps:donate:button:setup:wizard:unhosted:1',
  },
  WIZARD_STEP_TWO_UNHOSTED_PAGE: {
    step: 2,
    pgrp: 'main:wps:donate:button:setup:wizard:unhosted:2',
    page: 'main:wps:donate:button:setup:wizard:unhosted:2',
  },
  WIZARD_STEP_THREE_UNHOSTED_PAGE: {
    step: 3,
    pgrp: 'main:wps:donate:button:setup:wizard:unhosted:3',
    page: 'main:wps:donate:button:setup:wizard:unhosted:3',
  },
  WIZARD_STEP_FOUR_CAMPAIGN: {
    step: 4,
    pgrp: 'main:wps:donate:campaign:setup:wizard:4',
    page: 'main:wps:donate:campaign:setup:wizard:4',
  },
  WIZARD_STEP_ONE_CAMPAIGN: {
    step: 1,
    pgrp: 'main:wps:donate:campaign:setup:wizard:1',
    page: 'main:wps:donate:campaign:setup:wizard:1',
  },
  WIZARD_STEP_TWO_CAMPAIGN: {
    step: 2,
    pgrp: 'main:wps:donate:campaign:setup:wizard:2',
    page: 'main:wps:donate:campaign:setup:wizard:2',
  },
  WIZARD_STEP_THREE_CAMPAIGN: {
    step: 3,
    pgrp: 'main:wps:donate:campaign:setup:wizard:3',
    page: 'main:wps:donate:campaign:setup:wizard:3',
  },
};

const FLOW = {
  flow: isDonateButton() ? 'button' : 'link',
};

export const CAMPAIGN_FLOW = {
  flow: 'campaign',
};

const DEFAULT_FPTI_DATA = {
  charityName: getCharityName(),
  receiver_country: getReceiverCountry(),
  component: 'donatenodeweb',
  product: 'donate',
  account_type: getUserType(),
  encr_cust_id: getEncryptedAccountNum(), // check if this is generated automatically
  isLoggedIn: isLoggedInUser(),
  ...window?.fpti,
};

const LANDING_PAGE_DEFAULT_DATA = {
  ...DEFAULT_FPTI_DATA,
  ...PAGE_SPEC.LANDING_PAGE,
};

const MANAGE_PAGE_DEFAULT_DATA = {
  ...DEFAULT_FPTI_DATA,
  ...PAGE_SPEC.MANAGE_PAGE,
};

const CONFIGURE_DONATION_PAGE_DEFAULT_DATA = {
  step: 1,
  ...DEFAULT_FPTI_DATA,
  ...(isHosted
    ? { ...PAGE_SPEC.WIZARD_STEP_FOUR_HOSTED_PAGE }
    : { ...PAGE_SPEC.WIZARD_STEP_FOUR_UNHOSTED_PAGE }),
  ...(isCampaignFlow
    ? { ...CAMPAIGN_FLOW, ...PAGE_SPEC.WIZARD_STEP_FOUR_CAMPAIGN }
    : { ...FLOW }),
};

const EXPERIENCE_SETUP_PAGE_DEFAULT_DATA = {
  ...DEFAULT_FPTI_DATA,
  ...(isHosted
    ? { ...PAGE_SPEC.WIZARD_STEP_TWO_HOSTED_PAGE }
    : { ...PAGE_SPEC.WIZARD_STEP_TWO_UNHOSTED_PAGE }),
  ...(isCampaignFlow
    ? { ...CAMPAIGN_FLOW, ...PAGE_SPEC.WIZARD_STEP_TWO_CAMPAIGN }
    : { ...FLOW }),
};

const SET_AMOUNT_PAGE_DEFAULT_DATA = {
  ...DEFAULT_FPTI_DATA,
  ...(isHosted
    ? { ...PAGE_SPEC.WIZARD_STEP_THREE_HOSTED_PAGE }
    : { ...PAGE_SPEC.WIZARD_STEP_THREE_UNHOSTED_PAGE }),
  ...(isCampaignFlow
    ? { ...CAMPAIGN_FLOW, ...PAGE_SPEC.WIZARD_STEP_ONE_CAMPAIGN }
    : { ...FLOW }),
};

const ADVANCED_SETTINGS_PAGE_DEFAULT_DATA = {
  ...DEFAULT_FPTI_DATA,
  ...(isHosted
    ? { ...PAGE_SPEC.WIZARD_STEP_FOUR_HOSTED_PAGE }
    : { ...PAGE_SPEC.WIZARD_STEP_FOUR_UNHOSTED_PAGE }),
  ...(isCampaignFlow
    ? { ...CAMPAIGN_FLOW, ...PAGE_SPEC.WIZARD_STEP_THREE_CAMPAIGN }
    : { ...FLOW }),
};

const OVERVIEW_PAGE_DEFAULT_DATA = {
  ...DEFAULT_FPTI_DATA,
  ...(isCampaignFlow ? { ...CAMPAIGN_FLOW } : { ...FLOW }),
  ...PAGE_SPEC.OVERVIEW_PAGE,
};

const CLICK = {
  e: 'cl',
};

const IMPRESSION = {
  e: 'im',
};

const ERROR = {
  e: 'err',
};

const ACTIVITY = {
  e: 'ac',
};

export const FPTI_SPEC = {
  LANDING_PAGE_IM: {
    event_name: 'donate_setup_intro_screen_shown',
    data: {
      ...IMPRESSION,
      ...LANDING_PAGE_DEFAULT_DATA,
    },
  },
  LANDING_PAGE_ORG_UPDATES_MODULE_IM: {
    event_name: 'donate_donor_view_landing_updates_module_shown',
    data: {
      ...IMPRESSION,
      ...LANDING_PAGE_DEFAULT_DATA,
    },
  },
  LANDING_PAGE_LOGIN_CL: {
    event_name: 'donate_setup_intro_login_pressed',
    data: {
      ...CLICK,
      ...LANDING_PAGE_DEFAULT_DATA,
    },
  },
  LANDING_PAGE_SIGNUP_CL: {
    event_name: 'donate_setup_intro_sign_up_pressed',
    data: {
      ...CLICK,
      ...LANDING_PAGE_DEFAULT_DATA,
    },
  },
  LANDING_PAGE_HEADER_LOGIN_CL: {
    event_name: 'donate_setup_intro_header_login_pressed',
    data: {
      ...CLICK,
      ...LANDING_PAGE_DEFAULT_DATA,
    },
  },
  LANDING_PAGE_HEADER_SIGNUP_CL: {
    event_name: 'donate_setup_intro_header_sign_up_pressed',
    data: {
      ...CLICK,
      ...LANDING_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_PAGE_IM: {
    event_name: 'donate_setup_manage_screen_shown',
    data: {
      ...IMPRESSION,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_TABLE_PAGE_UPDATE: {
    event_name: 'donate_setup_manage_table_shown',
    data: {
      ...ACTIVITY,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_PAGE_CREATE_LINK_CL: {
    event_name: 'donate_setup_manage_create_link_pressed',
    data: {
      ...CLICK,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_PAGE_CREATE_CAMPAIGN_CL: {
    event_name: 'donate_setup_manage_create_campaign_pressed',
    data: {
      ...CLICK,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_PAGE_CREATE_BUTTON_CL: {
    event_name: 'donate_setup_manage_create_button_pressed',
    data: {
      ...CLICK,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_PAGE_MANAGE_CAMPAIGN_CL: {
    event_name: 'donate_setup_manage_saved_button_pressed',
    data: {
      ...CLICK,
      ...MANAGE_PAGE_DEFAULT_DATA,
      ...CAMPAIGN_FLOW,
    },
  },
  MANAGE_PAGE_MANAGE_BUTTON_CL: {
    event_name: 'donate_setup_manage_saved_button_pressed',
    data: {
      ...CLICK,
      ...MANAGE_PAGE_DEFAULT_DATA,
      ...FLOW,
    },
  },
  MANAGE_PAGE_ACCOUNT_UPGRADE_CL: {
    event_name: 'donate_setup_manage_account_upgrade_pressed',
    data: {
      ...CLICK,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_PAGE_INFO_OPTIONS_CL: {
    event_name: 'donate_setup_manage_page_options_pressed',
    data: {
      ...CLICK,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_PAGE_INFO_OPTIONS_CLOSED_CL: {
    event_name: 'donate_setup_available_pages_info_close_pressed',
    data: {
      ...CLICK,
      ...CONFIGURE_DONATION_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_PAGE_INFO_OPTIONS_IM: {
    event_name: 'donate_setup_available_pages_info_screen_shown',
    data: {
      ...IMPRESSION,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_PAGE_INFO_LINK_PRESSED: {
    event_name: 'donate_setup_available_pages_info_link_pressed',
    data: {
      ...CLICK,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_PAGE_INFO_BUTTON_PRESSED: {
    event_name: 'donate_setup_available_pages_info_button_pressed',
    data: {
      ...CLICK,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_PAGE_INFO_CAMPAIGN_PRESSED: {
    event_name: 'donate_setup_available_pages_info_campaign_pressed',
    data: {
      ...CLICK,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_PAGE_FEEDBACK_BUTTON_CL: {
    event_name: 'donate_setup_manage_feedback_pressed',
    data: {
      ...CLICK,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  CONFIGURE_DONATION_PAGE_IM: {
    event_name: 'donate_setup_configure_donation_page_screen_shown',
    data: {
      ...IMPRESSION,
      ...CONFIGURE_DONATION_PAGE_DEFAULT_DATA,
    },
  },
  CONFIGURE_DONATION_PAGE_NAV_CL: {
    event_name: 'donate_setup_configure_donation_page_nav_back_pressed',
    data: {
      ...CLICK,
      ...CONFIGURE_DONATION_PAGE_DEFAULT_DATA,
    },
  },
  CONFIGURE_DONATION_PAGE_BACK: {
    event_name: 'donate_setup_configure_donation_page_cancel_pressed',
    data: {
      ...CLICK,
      ...CONFIGURE_DONATION_PAGE_DEFAULT_DATA,
    },
  },
  CONFIGURE_DONATION_PAGE_FEEDBACK_CL: {
    event_name: 'donate_setup_configure_donation_page_feedback_pressed',
    data: {
      ...CLICK,
      ...CONFIGURE_DONATION_PAGE_DEFAULT_DATA,
    },
  },
  CONFIGURE_DONATION_PAGE_FINISH_CL: {
    event_name: 'donate_setup_configure_donation_page_continue_pressed',
    data: {
      ...CLICK,
      ...CONFIGURE_DONATION_PAGE_DEFAULT_DATA,
    },
  },
  CONFIGURE_DONATION_PAGE_ERROR_DISPLAY: {
    event_name: 'donate_setup_configure_donation_page_form_error_occurred',
    data: {
      ...ERROR,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  CONFIGURE_BUTTON_PAGE_CONTINUE_CL: {
    event_name: 'donate_setup_configure_donation_page_continue_pressed',
    data: {
      ...CLICK,
      ...CONFIGURE_DONATION_PAGE_DEFAULT_DATA,
    },
  },
  CONFIGURE_BUTTON_PAGE_CANCEL_CL: {
    event_name: 'donate_setup_configure_donation_page_cancel_pressed',
    data: {
      ...CLICK,
      ...CONFIGURE_DONATION_PAGE_DEFAULT_DATA,
    },
  },
  CONFIGURE_DONATION_PAGE_CANCEL_CL: {
    event_name: 'donate_setup_configure_donation_page_cancel_pressed',
    data: {
      ...CLICK,
      ...CONFIGURE_DONATION_PAGE_DEFAULT_DATA,
    },
  },
  CONFIGURE_BUTTON_PAGE_NAV_BACK_CL: {
    event_name: 'donate_setup_configure_donation_nav_back_pressed',
    data: {
      ...CLICK,
      ...CONFIGURE_DONATION_PAGE_DEFAULT_DATA,
    },
  },
  CONFIGURE_BUTTON_PAGE_FEEDBACK_CL: {
    event_name: 'donate_setup_configure_button_feedback_pressed',
    data: {
      ...CLICK,
      ...CONFIGURE_DONATION_PAGE_DEFAULT_DATA,
    },
  },
  CONFIGURE_BUTTON_PAGE_CHANGE_LOGO_CL: {
    event_name: 'donate_setup_configure_button_update_image_pressed',
    data: {
      ...CLICK,
      ...CONFIGURE_DONATION_PAGE_DEFAULT_DATA,
    },
  },
  CONFIGURE_BUTTON_PAGE_LOGO_UPDATED: {
    event_name: 'donate_setup_configure_button_image_updated',
    data: {
      ...ACTIVITY,
      ...CONFIGURE_DONATION_PAGE_DEFAULT_DATA,
    },
  },
  EXPERIENCE_SETUP_PAGE_IM: {
    event_name: 'donate_setup_experience_setup_screen_shown',
    data: {
      ...IMPRESSION,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  EXPERIENCE_SETUP_PAGE_CANCEL_CL: {
    event_name: 'donate_setup_experience_setup_cancel_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  EXPERIENCE_SETUP_PAGE_CONTINUE_CL: {
    event_name: 'donate_setup_experience_setup_continue_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  EXPERIENCE_SETUP_PAGE_ERROR_DISPLAY: {
    event_name: 'donate_setup_experience_form_error_occurred',
    data: {
      ...ERROR,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  EXPERIENCE_SETUP_PAGE_BACK_CL: {
    event_name: 'donate_setup_experience_setup_back_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  EXPERIENCE_SETUP_PAGE_NAV_BACK_CL: {
    event_name: 'donate_setup_experience_setup_nav_back_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  EXPERIENCE_SETUP_PAGE_FEEDBACK_CL: {
    event_name: 'donate_setup_experience_setup_feedback_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  EXPERIENCE_SETUP_PAGE_PREVIEW_TAB_CL: {
    event_name: 'donate_setup_experience_setup_tab_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  EXPERIENCE_SETUP_PAGE_UPLOAD_LOGO_CL: {
    event_name: 'donate_setup_experience_upload_logo_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  UPLOAD_LOGO_MODAL_IM: {
    event_name: 'donate_setup_upload_logo_screen_shown',
    data: {
      ...IMPRESSION,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  UPLOAD_BUTTON_IMAGE_SAVE_CL: {
    event_name: 'donate_setup_upload_button_image_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  UPLOAD_LOGO_MODAL_SAVE_CL: {
    event_name: 'donate_setup_upload_logo_save_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  UPLOAD_LOGO_MODAL_IMAGE_UPDATED: {
    event_name: 'donate_setup_upload_logo_image_updated',
    data: {
      ...ACTIVITY,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  UPLOAD_LOGO_MODAL_CHECKBOX_CL: {
    event_name: 'donate_setup_upload_logo_checkbox_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  UPLOAD_LOGO_MODAL_DISMISS_CL: {
    event_name: 'donate_setup_upload_logo_dismiss_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  EXPERIENCE_SETUP_PAGE_UPLOAD_BACKGROUND_IMG_CL: {
    event_name: 'donate_setup_experience_upload_img_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  UPLOAD_BACKGROUND_IMG_MODAL_IM: {
    event_name: 'donate_setup_upload_img_screen_shown',
    data: {
      ...IMPRESSION,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  UPLOAD_BACKGROUND_IMG_MODAL_SAVE_CL: {
    event_name: 'donate_setup_upload_img_save_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  UPLOAD_BACKGROUND_IMG_MODAL_IMAGE_UPDATED: {
    event_name: 'donate_setup_upload_img_img_updated',
    data: {
      ...ACTIVITY,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  UPLOAD_BACKGROUND_IMG_MODAL_DISMISS_CL: {
    event_name: 'donate_setup_upload_img_dismiss_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  UPLOAD_BACKGROUND_IMG_MODAL_DISMISS_ERROR: {
    event_name: 'donate_setup_upload_img_error_occurred',
    data: {
      ...ERROR,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  SET_AMOUNT_PAGE_IM: {
    event_name: 'donate_setup_set_amount_screen_shown',
    data: {
      ...IMPRESSION,
      ...SET_AMOUNT_PAGE_DEFAULT_DATA,
    },
  },
  SET_AMOUNT_PAGE_CONTINUE_CL: {
    event_name: 'donate_setup_set_amount_continue_pressed',
    data: {
      ...CLICK,
      ...SET_AMOUNT_PAGE_DEFAULT_DATA,
    },
  },
  SET_AMOUNT_PAGE_FINISH_CL: {
    event_name: 'donate_setup_set_amount_finish_pressed',
    data: {
      ...CLICK,
      ...SET_AMOUNT_PAGE_DEFAULT_DATA,
    },
  },
  SET_AMOUNT_PAGE_BACK_CL: {
    event_name: 'donate_setup_set_amount_back_pressed',
    data: {
      ...CLICK,
      ...SET_AMOUNT_PAGE_DEFAULT_DATA,
    },
  },
  SET_AMOUNT_PAGE_NAV_BACK_CL: {
    event_name: 'donate_setup_set_amount_nav_back_pressed',
    data: {
      ...CLICK,
      ...SET_AMOUNT_PAGE_DEFAULT_DATA,
    },
  },
  SET_AMOUNT_PAGE_FEEDBACK_CL: {
    event_name: 'donate_setup_set_amount_feedback_pressed',
    data: {
      ...CLICK,
      ...SET_AMOUNT_PAGE_DEFAULT_DATA,
    },
  },
  SET_AMOUNT_ERROR_DISPLAY: {
    event_name: 'donate_setup_set_amount_form_error_occurred',
    data: {
      ...ERROR,
      ...SET_AMOUNT_PAGE_DEFAULT_DATA,
    },
  },
  SET_AMOUNT_PAGE_PREVIEW_TAB_CL: {
    event_name: 'donate_setup_set_amount_tab_pressed',
    data: {
      ...CLICK,
      ...SET_AMOUNT_PAGE_DEFAULT_DATA,
    },
  },
  ADVANCED_SETTINGS_PAGE_IM: {
    event_name: 'donate_setup_advanced_settings_screen_shown',
    data: {
      ...IMPRESSION,
      ...ADVANCED_SETTINGS_PAGE_DEFAULT_DATA,
    },
  },
  ADVANCED_SETTINGS_PAGE_FINISH_CL: {
    event_name: 'donate_setup_advanced_settings_next_pressed',
    data: {
      ...CLICK,
      ...ADVANCED_SETTINGS_PAGE_DEFAULT_DATA,
    },
  },
  ADVANCED_SETTINGS_PAGE_ERROR_DISPLAY: {
    event_name: 'donate_setup_advanced_settings_form_error_occurred',
    data: {
      ...CLICK,
      ...ADVANCED_SETTINGS_PAGE_DEFAULT_DATA,
    },
  },
  ADVANCED_SETTINGS_PAGE_BACK_CL: {
    event_name: 'donate_setup_advanced_settings_back_pressed',
    data: {
      ...CLICK,
      ...ADVANCED_SETTINGS_PAGE_DEFAULT_DATA,
    },
  },
  ADVANCED_SETTINGS_PAGE_NAV_BACK_CL: {
    event_name: 'donate_setup_advanced_settings_nav_back_pressed',
    data: {
      ...CLICK,
      ...ADVANCED_SETTINGS_PAGE_DEFAULT_DATA,
    },
  },
  ADVANCED_SETTINGS_PAGE_FEEDBACK_CL: {
    event_name: 'donate_setup_advanced_settings_feedback_pressed',
    data: {
      ...CLICK,
      ...ADVANCED_SETTINGS_PAGE_DEFAULT_DATA,
    },
  },
  ADVANCED_SETTINGS_PAGE_PREVIEW_TAB_CL: {
    event_name: 'donate_setup_advanced_settings_tab_pressed',
    data: {
      ...CLICK,
      ...ADVANCED_SETTINGS_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_IM: {
    event_name: 'donate_setup_donation_page_details_view',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_SUCCESS_IM: {
    event_name: 'donate_setup_donation_page_creation_success',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  CAMPAIGN_OVERVIEW_PAGE_RISK_SUCCESS_IM: {
    event_name: 'donate_campaign_setup_donation_page_risk_success',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  CAMPAIGN_OVERVIEW_PAGE_RISK_DENY_IM: {
    event_name: 'donate_campaign_setup_donation_page_risk_denied',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  CAMPAIGN_OVERVIEW_PAGE_RISK_SERVER_ERROR_IM: {
    event_name: 'donate_campaign_setup_donation_page_risk_server_error',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  CAMPAIGN_OVERVIEW_PAGE_STEPUP_SUCCESS_IM: {
    event_name: 'donate_campaign_setup_donation_page_stepup_success',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  CAMPAIGN_OVERVIEW_PAGE_STEPUP_DENY_IM: {
    event_name: 'donate_campaign_setup_donation_page_stepup_denied',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  CAMPAIGN_OVERVIEW_PAGE_STEPUP_SERVER_ERROR_IM: {
    event_name: 'donate_campaign_setup_donation_page_stepup_server_error',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  DONATE_CAMPAIGN_STEPUP_REDIRECT: {
    event_name: 'donate_setup_campaign_stepup_redirect',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  BUTTON_OVERVIEW_PAGE_RISK_SUCCESS_IM: {
    event_name: 'donate_button_setup_donation_page_risk_success',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  BUTTON_OVERVIEW_PAGE_RISK_DENY_IM: {
    event_name: 'donate_button_setup_donation_page_risk_denied',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  BUTTON_OVERVIEW_PAGE_RISK_SERVER_ERROR_IM: {
    event_name: 'donate_button_setup_donation_page_risk_server_error',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  BUTTON_OVERVIEW_PAGE_STEPUP_SUCCESS_IM: {
    event_name: 'donate_button_setup_donation_page_stepup_success',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  BUTTON_OVERVIEW_PAGE_STEPUP_DENY_IM: {
    event_name: 'donate_button_setup_donation_page_stepup_denied',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  BUTTON_OVERVIEW_PAGE_STEPUP_SERVER_ERROR_IM: {
    event_name: 'donate_button_setup_donation_page_stepup_server_error',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  DONATE_BUTTON_STEPUP_REDIRECT: {
    event_name: 'donate_setup_button_stepup_redirect',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_FINISH_CL: {
    event_name: 'donate_setup_donation_page_finish_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_ERROR_FINISH_CL: {
    event_name: 'donate_setup_donation_page_finish_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_BUTTON_INTERSITIAL_IM: {
    event_name: 'donate_setup_create_button_interstitial_shown',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_CAMPAIGN_INTERSITIAL_IM: {
    event_name: 'donate_setup_create_campaign_interstitial_shown',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_LINK_INTERSITIAL_IM: {
    event_name: 'donate_setup_create_link_interstitial_shown',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_CREATE_QRC_INTERSITIAL_IM: {
    event_name: 'donate_setup_create_qrc_interstitial_shown',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_ORG_UPDATES_IM: {
    event_name: 'donate_setup_manage_updates_module_shown',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
      state: 'populated',
    },
  },
  OVERVIEW_PAGE_ORG_UPDATES_LIST_IM: {
    event_name: 'donate_setup_update_list_screen_shown',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_ORG_UPDATES_DETAIL_IM: {
    event_name: 'donate_setup_update_detail_screen_shown',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_ORG_UPDATES_CREATE_IM: {
    event_name: 'donate_setup_create_update_screen_shown',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
      page_variant: 'post_new_update',
    },
  },
  OVERVIEW_PAGE_ORG_UPDATES_EDIT_IM: {
    event_name: 'donate_setup_edit_update_screen_shown',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
      page_variant: 'edit_existing_update',
    },
  },
  OVERVIEW_PAGE_ORG_UPDATES_SHARE_IM: {
    event_name: 'donate_setup_share_update_screen_shown',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_ORG_UPDATES_CONFIRM_DELETE_IM: {
    event_name: 'donate_setup_delete_update_screen_shown',
    data: {
      ...ACTIVITY,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_ORG_UPDATES_CREATE_SUCCESS_AC: {
    event_name: 'donate_setup_post_update_success_occurred',
    data: {
      ...ACTIVITY,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_ORG_UPDATES_DELETE_SUCCESS_AC: {
    event_name: 'donate_setup_delete_update_success_occurred',
    data: {
      ...ACTIVITY,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_ORG_UPDATES_CREATE_GENERIC_ERROR_AC: {
    event_name: 'donate_setup_update_generic_error_shown',
    data: {
      ...ACTIVITY,
      ...ERROR,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_SUCCESS_BANNER_IM: {
    event_name: 'donate_setup_success_banner_shown',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_SUCCESS_BANNER_SAVED_BUTTON_CL: {
    event_name: 'donate_setup_success_banner_donation_page_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_SUCCESS_BANNER_DISMISS_CL: {
    event_name: 'donate_setup_success_banner_closed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_BACK_CL: {
    event_name: 'donate_setup_success_back_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_EDIT_NAME_CL: {
    event_name: 'donate_setup_success_edit_name_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_SAVE_NAME_CL: {
    event_name: 'donate_setup_success_save_name_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_EDIT_CL: {
    event_name: 'donate_setup_success_edit_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_PREVIEW_CL: {
    event_name: 'donate_setup_success_preview_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_POST_UPDATE_CL: {
    event_name: 'donate_setup_manage_updates_module_post_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_LIST_POST_UPDATE_CL: {
    event_name: 'donate_setup_update_list_post_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_LIST_ITEM_PRESSED_CL: {
    event_name: 'donate_setup_update_list_item_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_POST_UPDATE_LINK_CL: {
    event_name: 'donate_setup_manage_post_update_link_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_UPDATE_EDIT_PRESSED_CL: {
    event_name: 'donate_setup_update_detail_edit_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_UPDATE_POST_PRESSED_CL: {
    event_name: 'donate_setup_create_update_post_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
      page_variant: 'post_new_update',
    },
  },
  OVERVIEW_PAGE_UPDATE_SAVE_PRESSED_CL: {
    event_name: 'donate_setup_create_update_save_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
      page_variant: 'edit_existing_update',
    },
  },
  OVERVIEW_PAGE_UPDATE_DELETE_PRESSED_CL: {
    event_name: 'donate_setup_create_update_delete_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
      page_variant: 'edit_existing_update',
    },
  },
  OVERVIEW_PAGE_UPDATE_POST_CANCEL_PRESSED_CL: {
    event_name: 'donate_setup_create_update_cancel_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
      page_variant: 'post_new_update',
    },
  },
  OVERVIEW_PAGE_UPDATE_EDIT_CANCEL_PRESSED_CL: {
    event_name: 'donate_setup_create_update_cancel_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
      page_variant: 'edit_existing_update',
    },
  },
  OVERVIEW_PAGE_UPDATE_SHARE_CHANNERL_PRESSED_CL: {
    event_name: 'donate_setup_share_update_channel_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_UPDATE_DELETE_CONFIRM_PRESSED_CL: {
    event_name: 'donate_setup_delete_update_delete_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_UPDATE_DELETE_KEEP_PRESSED_CL: {
    event_name: 'donate_setup_delete_update_keep_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_UPDATE_START_OVER_PRESSED_CL: {
    event_name: 'donate_setup_update_generic_error_start_over_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
      page_variant: 'post_new_update',
    },
  },
  OVERVIEW_PAGE_UPDATE_TRY_AGAIN_PRESSED_CL: {
    event_name: 'donate_setup_update_generic_error_try_again_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
      page_variant: 'post_new_update',
    },
  },
  OVERVIEW_PAGE_UPDATE_FORM_ERROR_AC: {
    event_name: 'donate_setup_create_update_form_error_occurred',
    data: {
      ...ACTIVITY,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_PREVIEW_DISMISS_CL: {
    event_name: 'donate_setup_success_preview_dismiss_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_DUPLICATE_CL: {
    event_name: 'donate_setup_success_duplicate_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_DELETE_CL: {
    event_name: 'donate_setup_success_delete_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_SHARE_CL: {
    event_name: 'donate_setup_success_share_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_COPY_LINK_CL: {
    event_name: 'donate_setup_success_copy_link_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_COPY_BUTTON_CL: {
    event_name: 'donate_setup_success_copy_button_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_COPY_WIDGET_CL: {
    event_name: 'donate_setup_success_copy_embed_card_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_DOWNLOAD_QRC_CL: {
    event_name: 'donate_setup_success_download_qrc_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_FEEDBACK_CL: {
    event_name: 'donate_setup_success_feedback_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_SHAREABLE_LINK: {
    event_name: 'donate_setup_success_shareable_link_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_QR_CODE: {
    event_name: 'donate_setup_success_qr_code_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_DONATE_BUTTON: {
    event_name: 'donate_setup_success_donate_button_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_CAMPAIGN_CARD: {
    event_name: 'donate_setup_success_campaign_card_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_SHOW_MORE_UPDATES_CL: {
    event_name: 'donate_setup_manage_updates_module_show_more_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
      state: 'populated',
    },
  },
  OVERVIEW_PAGE_SHOW_PREVIOUS_UPDATES_CL: {
    event_name: 'donate_setup_update_list_show_previous_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_DISMISS_UPDATES_LIST_SHEET_CL: {
    event_name: 'donate_setup_update_list_dismiss_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      dismiss_method: 'close',
    },
  },
  OVERVIEW_PAGE_DISMISS_UPDATES_DETAIL_SHEET_CL: {
    event_name: 'donate_setup_update_detail_dismiss_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      dismiss_method: 'close',
    },
  },
  OVERVIEW_PAGE_DISMISS_UPDATES_CREATE_SHEET_CL: {
    event_name: 'donate_setup_create_update_dismiss_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      dismiss_method: 'close',
    },
  },
  OVERVIEW_PAGE_DISMISS_UPDATES_DELETE_SHEET_CL: {
    event_name: 'donate_setup_delete_update_dismiss_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      dismiss_method: 'close',
    },
  },
  OVERVIEW_PAGE_DISMISS_UPDATES_SHARE_SHEET_CL: {
    event_name: 'donate_setup_share_update_dismiss_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      dismiss_method: 'close',
    },
  },
  OVERVIEW_PAGE_DISMISS_BUTTON_INTERSITIAL_CL: {
    event_name: 'donate_setup_create_button_interstitial_dismiss_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      dismiss_method: 'close',
    },
  },
  OVERVIEW_PAGE_DISMISS_CAMPAIGN_INTERSITIAL_CL: {
    event_name: 'donate_setup_create_campaign_interstitial_dismiss_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      dismiss_method: 'close',
    },
  },
  OVERVIEW_PAGE_DISMISS_LINK_INTERSITIAL_CL: {
    event_name: 'donate_setup_create_link_interstitial_dismiss_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      dismiss_method: 'close',
    },
  },
  OVERVIEW_PAGE_DISMISS_QRC_INTERSITIAL_CL: {
    event_name: 'donate_setup_create_qrc_interstitial_dismiss_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      dismiss_method: 'close',
    },
  },
  PRODUCT_UPDATE_EXPERIENCE_MODAL_IM: {
    event_name: 'donate_setup_feature_education_screen_shown',
    data: {
      ...IMPRESSION,
      ...DEFAULT_FPTI_DATA,
    },
  },
  PRODUCT_UPDATE_EXPERIENCE_MODAL_SIDE_SCROLL_CL: {
    event_name: 'donate_setup_feature_education_side_scroll_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
    },
  },
  PRODUCT_UPDATE_EXPERIENCE_MODAL_DISMISS_CL: {
    event_name: 'donate_setup_feature_education_dismiss_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
    },
  },
  ACTIONABLE_ERROR_MODAL_IM: {
    event_name: 'donate_setup_actionable_error_modal_shown',
    data: {
      ...IMPRESSION,
      ...DEFAULT_FPTI_DATA,
      ...FLOW,
    },
  },
  ACTIONABLE_ERROR_MODAL_REVIEW_CL: {
    event_name: 'donate_setup_actionable_error_edit_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      ...FLOW,
    },
  },
  ACTIONABLE_ERROR_MODAL_START_OVER_CL: {
    event_name: 'donate_setup_actionable_error_start_over_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      ...FLOW,
    },
  },
  ACTIONABLE_ERROR_MODAL_DISMISS_CL: {
    event_name: 'donate_setup_actionable_error_dismiss_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      ...FLOW,
    },
  },
  FIELD_LEVEL_ERROR_IM: {
    event_name: 'donate_setup_input_validation_error_occurred',
    data: {
      ...ERROR,
      ...DEFAULT_FPTI_DATA,
      ...FLOW,
    },
  },
  ERROR_INTERACTION_IM: {
    event_name: 'donate_setup_failed_customer_interaction_occurred',
    data: {
      ...ERROR,
      ...DEFAULT_FPTI_DATA,
    },
  },
  ERROR_INTERACTION_LINK_CL: {
    event_name: 'donate_setup_failed_customer_interaction_link_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
    },
  },
  ERROR_INTERACTION_DISMISS_CL: {
    event_name: 'donate_setup_failed_customer_interaction_dismiss_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
    },
  },
  ALERT_MODAL_IM: {
    event_name: 'donate_setup_manage_action_popup_shown',
    data: {
      ...IMPRESSION,
      ...DEFAULT_FPTI_DATA,
      ...(isCampaignFlow ? { ...CAMPAIGN_FLOW } : { ...FLOW }),
    },
  },
  ALERT_MODAL_CONFIRM_CL: {
    event_name: 'donate_setup_manage_action_popup_confirm_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      ...(isCampaignFlow ? { ...CAMPAIGN_FLOW } : { ...FLOW }),
    },
  },
  ALERT_MODAL_CANCEL_CL: {
    event_name: 'donate_setup_manage_action_popup_cancel_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      ...(isCampaignFlow ? { ...CAMPAIGN_FLOW } : { ...FLOW }),
    },
  },
  ALERT_MODAL_FINISH_CL: {
    event_name: 'donate_setup_manage_action_popup_finish_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      ...(isCampaignFlow ? { ...CAMPAIGN_FLOW } : { ...FLOW }),
    },
  },
  ALERT_MODAL_SAVE_CL: {
    event_name: 'donate_setup_manage_action_popup_save_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      ...(isCampaignFlow ? { ...CAMPAIGN_FLOW } : { ...FLOW }),
    },
  },
  ALERT_MODAL_DELETE_CL: {
    event_name: 'donate_setup_manage_action_popup_delete_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      ...(isCampaignFlow ? { ...CAMPAIGN_FLOW } : { ...FLOW }),
    },
  },
  ALERT_MODAL_TRY_AGAIN_CL: {
    event_name: 'donate_setup_manage_action_popup_try_again',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      ...(isCampaignFlow ? { ...CAMPAIGN_FLOW } : { ...FLOW }),
    },
  },
  ALERT_MODAL_QUIT_CL: {
    event_name: 'donate_setup_manage_action_popup_quit',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      ...(isCampaignFlow ? { ...CAMPAIGN_FLOW } : { ...FLOW }),
    },
  },
  ALERT_MODAL_DISMISS_CL: {
    event_name: 'donate_setup_manage_action_popup_dismiss_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      ...(isCampaignFlow ? { ...CAMPAIGN_FLOW } : { ...FLOW }),
    },
  },
  ACCOUNT_RESTRICTED_PAGE_IM: {
    event_name: 'donate_setup_account_restricted_error_occurred',
    data: {
      ...IMPRESSION,
      ...DEFAULT_FPTI_DATA,
    },
  },
  DONATE_SETUP_ACTION_OCURRED: {
    event_name: 'donate_setup_manage_action_occurred',
    data: {
      ...ACTIVITY,
      ...DEFAULT_FPTI_DATA,
      ...CAMPAIGN_FLOW,
    },
  },
  DONATE_SETUP_SUCCESS_CLOSE_REOPEN_CAMPAIGN: {
    event_name: 'donate_setup_success_close_reopen_toggle_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      ...CAMPAIGN_FLOW,
    },
  },
  MANAGE_STATISTICS_MODULE_IM: {
    event_name: 'donate_setup_manage_statistics_module_shown',
    data: {
      ...IMPRESSION,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_STATISTICS_UNSAVED_TOOLTIP_IM: {
    event_name: 'donate_setup_manage_statistics_unsaved_tooltip_shown',
    data: {
      ...IMPRESSION,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_STATISTICS_UNSAVED_TOOLTIP_CL: {
    event_name: 'donate_setup_manage_statistics_unsaved_tooltip_pressed',
    data: {
      ...CLICK,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_TIMEFRAME_DROPDOWN_CL: {
    event_name: 'donate_setup_manage_time_frame_dropdown_pressed',
    data: {
      ...CLICK,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_TIMEFRAME_LIST_IM: {
    event_name: 'donate_setup_manage_time_frame_list_shown',
    data: {
      ...IMPRESSION,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },

  MANAGE_TIMEFRAME_SELECTION_CL: {
    event_name: 'donate_setup_manage_time_frame_selection_pressed',
    data: {
      ...CLICK,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_CURRENCY_BREAKDOWN_CL: {
    event_name: 'donate_setup_manage_currency_breakdown_pressed',
    data: {
      ...CLICK,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_CURRENCY_BREAKDOWN_SHEET_IM: {
    event_name: 'donate_received_currency_breakdown_screen_shown',
    data: {
      ...IMPRESSION,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_ZERO_RAISED_MODULE_IM: {
    event_name: 'donate_setup_manage_zero_raised_module_shown',
    data: {
      ...IMPRESSION,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_ZERO_RAISED_MODULE_SHARE_CL: {
    event_name: 'donate_setup_manage_zero_raised_module_share_pressed',
    data: {
      ...CLICK,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_PAGE_EDIT_CL: {
    event_name: 'donate_setup_success_edit_pressed',
    data: {
      ...CLICK,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_PAGE_DELETE_CL: {
    event_name: 'donate_setup_success_delete_pressed',
    data: {
      ...CLICK,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  FULL_PAGE_ERROR: {
    event_name: 'donate_setup_full_page_error_occurred',
    data: {
      ...ACTIVITY,
      ...DEFAULT_FPTI_DATA,
    },
  },
  MANAGE_SUMMARY_MODULE_ERROR_IM: {
    event_name: 'donate_setup_manage_summary_module_error_shown',
    data: {
      ...IMPRESSION,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_SUMMARY_MODULE_RETRY_CL: {
    event_name: 'donate_setup_manage_summary_module_error_retry_pressed',
    data: {
      ...CLICK,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_TABLE_INSIGHT_ERROR_IM: {
    event_name: 'donate_setup_manage_table_insight_error_shown',
    data: {
      ...IMPRESSION,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_TABLE_INSIGHT_RETRY_CL: {
    event_name: 'donate_setup_manage_table_insight_error_retry_pressed',
    data: {
      ...CLICK,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  DONATE_SETUP_BANNER_SHOWN: {
    event_name: 'donate_setup_banner_shown',
    data: {
      ...ERROR,
      ...DEFAULT_FPTI_DATA,
    },
  },
  DONATE_SETUP_BANNER_CLICKED: {
    event_name: 'donate_setup_banner_clicked',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
    },
  },
  DONATE_SETUP_WELCOME_CARD_SHOWN: {
    event_name: 'donate_setup_welcome_card_shown',
    data: {
      ...ERROR,
      ...DEFAULT_FPTI_DATA,
    },
  },
  DONATE_SETUP_WELCOME_CARD_CLICKED: {
    event_name: 'donate_setup_welcome_card_clicked',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
    },
  },
  SET_AMOUNT_PAGE_SAVE_IM: {
    event_name: 'donate_setup_set_amount_save_shown',
    data: {
      ...IMPRESSION,
      ...DEFAULT_FPTI_DATA,
      ...(isCampaignFlow ? { ...CAMPAIGN_FLOW } : { ...FLOW }),
    },
  },
  EXPERIENCE_SETUP_PAGE_SAVE_IM: {
    event_name: 'donate_setup_experience_setup_save_shown',
    data: {
      ...IMPRESSION,
      ...DEFAULT_FPTI_DATA,
      ...(isCampaignFlow ? { ...CAMPAIGN_FLOW } : { ...FLOW }),
    },
  },
  ADVANCED_SETTINGS_PAGE_SAVE_IM: {
    event_name: 'donate_setup_advanced_settings_save_shown',
    data: {
      ...IMPRESSION,
      ...DEFAULT_FPTI_DATA,
      ...(isCampaignFlow ? { ...CAMPAIGN_FLOW } : { ...FLOW }),
    },
  },
  CONFIGURE_DONATION_PAGE_SAVE_IM: {
    event_name: 'donate_setup_configure_donation_page_save_shown',
    data: {
      ...IMPRESSION,
      ...DEFAULT_FPTI_DATA,
      ...(isCampaignFlow ? { ...CAMPAIGN_FLOW } : { ...FLOW }),
    },
  },

  SET_AMOUNT_PAGE_SAVE_CL: {
    event_name: 'donate_setup_set_amount_save_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      ...(isCampaignFlow ? { ...CAMPAIGN_FLOW } : { ...FLOW }),
    },
  },
  EXPERIENCE_SETUP_PAGE_SAVE_CL: {
    event_name: 'donate_setup_experience_setup_save_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      ...(isCampaignFlow ? { ...CAMPAIGN_FLOW } : { ...FLOW }),
    },
  },
  ADVANCED_SETTINGS_PAGE_SAVE_CL: {
    event_name: 'donate_setup_advanced_settings_save_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      ...(isCampaignFlow ? { ...CAMPAIGN_FLOW } : { ...FLOW }),
    },
  },
  CONFIGURE_DONATION_PAGE_SAVE_CL: {
    event_name: 'donate_setup_configure_donation_page_save_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      ...(isCampaignFlow ? { ...CAMPAIGN_FLOW } : { ...FLOW }),
    },
  },

  SET_AMOUNT_PAGE_SAVE_ERROR_IM: {
    event_name: 'donate_setup_set_amount_save_error_shown',
    data: {
      ...IMPRESSION,
      ...DEFAULT_FPTI_DATA,
      ...(isCampaignFlow ? { ...CAMPAIGN_FLOW } : { ...FLOW }),
    },
  },
  EXPERIENCE_SETUP_PAGE_SAVE_ERROR_IM: {
    event_name: 'donate_setup_experience_setup_save_error_shown',
    data: {
      ...IMPRESSION,
      ...DEFAULT_FPTI_DATA,
      ...(isCampaignFlow ? { ...CAMPAIGN_FLOW } : { ...FLOW }),
    },
  },
  ADVANCED_SETTINGS_PAGE_SAVE_ERROR_IM: {
    event_name: 'donate_setup_advanced_settings_save_error_shown',
    data: {
      ...IMPRESSION,
      ...DEFAULT_FPTI_DATA,
      ...(isCampaignFlow ? { ...CAMPAIGN_FLOW } : { ...FLOW }),
    },
  },
  CONFIGURE_DONATION_PAGE_SAVE_ERROR_IM: {
    event_name: 'donate_setup_configure_donation_page_save_error_shown',
    data: {
      ...IMPRESSION,
      ...DEFAULT_FPTI_DATA,
      ...(isCampaignFlow ? { ...CAMPAIGN_FLOW } : { ...FLOW }),
    },
  },
  DONATE_SETUP_WIZARD_PROJECT_NAME_SHOWN: {
    event_name: 'donate_setup_wizard_project_name_shown',
    data: {
      ...IMPRESSION,
      ...CONFIGURE_DONATION_PAGE_DEFAULT_DATA,
    },
  },
  DONATE_SETUP_WIZARD_PROJECT_NAME_UPDATED: {
    event_name: 'donate_setup_wizard_project_name_updated',
    data: {
      ...CLICK,
      ...CONFIGURE_DONATION_PAGE_DEFAULT_DATA,
    },
  },
  DONATE_SETUP_NO_BUTTON_PENDING_CONTENT_SHOWN: {
    event_name: 'donate_setup_no_button_pending_content_shown',
    data: {
      ...ERROR,
      ...DEFAULT_FPTI_DATA,
    },
  },
  DONATE_SETUP_VERIFIED_FIRST_TIME_VISIT_CONTENT_SHOWN: {
    event_name: 'donate_setup_verified_first_time_visit_content_shown',
    data: {
      ...ERROR,
      ...DEFAULT_FPTI_DATA,
    },
  },
  DONATE_SETUP_PENDING_BANNER_SHOWN: {
    event_name: 'donate_setup_pending_banner_shown',
    data: {
      ...ERROR,
      ...DEFAULT_FPTI_DATA,
    },
  },
  DONATE_SETUP_PENDING_BANNER_CLICKED: {
    event_name: 'donate_setup_pending_banner_clicked',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
    },
  },
  EXPERIENCE_SETUP_PAGE_ENHANCE_PURPOSE_CL: {
    event_name: 'donate_setup_experience_setup_enhance_purpose_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
};

export const ACTION = {
  EXTEND_SESSION: 'extend session',
  QUIT: 'quit creating donation page',
  RETRY_SAVE: 'could not save draft',
  DELETE: 'delete donation page',
  CLOSE_CAMPAIGN: 'close_campaign',
  REOPEN_CAMPAIGN: 'reopen_campaign',
  DELETE_CAMPAIGN: 'delete_campaign',
  DUPLICATE: 'duplicate',
  EDIT_PAGE: 'edit_page',
  FINISH_PAGE: 'finish_page',
  PUBLISH_PAGE: 'publish_page',
};

export const SCREEN = {
  CONFIGURE_BUTTON: 'configure_button',
  EXPERIENCE_SETUP: 'experience_setup',
  SET_AMOUNT: 'set_amount',
  ADVANCED_SETTINGS: 'advanced_settings',
  MANAGE: 'manage',
  SUCCESS: 'success',
};

export const RTE_FPTI_ACTION_SPEC = {
  EXPERIENCE_SETUP_PAGE_UPLOAD_BACKGROUND_IMG_CL: {
    event_name: 'donate_setup_experience_upload_img_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  UPLOAD_BACKGROUND_IMG_MODAL_IM: {
    event_name: 'donate_setup_upload_img_screen_shown',
    data: {
      ...IMPRESSION,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  UPLOAD_BACKGROUND_IMG_MODAL_SAVE_CL: {
    event_name: 'donate_setup_upload_img_save_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  UPLOAD_BACKGROUND_IMG_MODAL_IMAGE_UPDATED: {
    event_name: 'donate_setup_upload_img_img_updated',
    data: {
      ...ACTIVITY,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  UPLOAD_BACKGROUND_IMG_MODAL_DISMISS_CL: {
    event_name: 'donate_setup_upload_img_dismiss_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  UPLOAD_BACKGROUND_IMG_MODAL_DISMISS_ERROR: {
    event_name: 'donate_setup_upload_img_error_occurred',
    data: {
      ...ERROR,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  DONATE_SETUP_ADD_LINK_IMP: {
    event_name: 'donate_setup_add_link_screen_shown',
    data: {
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
      ...IMPRESSION,
    },
  },
  DONATE_SETUP_ADD_LINK_CL: {
    event_name: 'donate_setup_add_link_add_pressed',
    data: {
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
      ...CLICK,
    },
  },
  DONATE_SETUP_ADD_LINK_CANCEL: {
    event_name: 'donate_setup_add_link_cancel_pressed',
    data: {
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
      ...CLICK,
    },
  },
  DONATE_SETUP_ADD_LINK_DISMISS: {
    event_name: 'donate_setup_add_link_dismiss_pressed',
    data: {
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
      ...CLICK,
    },
  },
  DONATE_SETUP_ADD_LINK_ERROR: {
    event_name: 'donate_setup_add_link_form_error_occurred',
    data: {
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
      ...ERROR,
    },
  },
  DONATE_SETUP_ADD_VIDEO_IMP: {
    event_name: 'donate_setup_add_video_screen_shown',
    data: {
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
      ...IMPRESSION,
    },
  },
  DONATE_SETUP_ADD_VIDEO_CL: {
    event_name: 'donate_setup_add_video_add_pressed',
    data: {
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
      ...CLICK,
    },
  },
  DONATE_SETUP_ADD_VIDEO_CANCEL: {
    event_name: 'donate_setup_add_video_cancel_pressed',
    data: {
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
      ...CLICK,
    },
  },
  DONATE_SETUP_ADD_VIDEO_DISMISS: {
    event_name: 'donate_setup_add_video_dismiss_pressed',
    data: {
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
      ...CLICK,
    },
  },
  EXPAND_TEXT_EDITOR: {
    event_name: 'donate_setup_experience_setup_expand_editor_pressed',
    data: {
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
      ...CLICK,
    },
  },
  ERROR_TEXT_EDITOR: {
    event_name: 'donate_setup_experience_setup_expand_editor_pressed',
    data: {
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
      ...ERROR,
    },
  },
  DONATE_SETUP_ADD_VIDEO_ERROR: {
    event_name: 'donate_setup_add_video_form_error_occurred',
    data: {
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
      ...ERROR,
    },
  },
  DONATE_SETUP_TEXT_EDITOR_ERROR: {
    event_name: 'donate_setup_experience_setup_text_editor',
    data: {
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
      ...ERROR,
    },
  },
};
